import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

function normalizeRole(role) {
  const raw = String(role || "").trim().toLowerCase();

  const aliases = {
    administrator: "admin",
    superadmin: "admin",
    "course lead": "course_lead",
    "faculty member": "faculty",
    instructor: "faculty",
    teacher: "faculty",
    "qec officer": "qec",
    "quality officer": "qec",
  };

  return aliases[raw] || raw;
}

function parseCLOs(value) {
  if (!value) return [];

  let data = value;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return data
        .split(/\n|,/)
        .map((x) => x.trim())
        .filter(Boolean)
        .map((text, index) => ({
          code: `CLO${index + 1}`,
          text,
        }));
    }
  }

  if (Array.isArray(data)) {
    return data
      .map((item, index) => {
        if (typeof item === "string") {
          return {
            code: `CLO${index + 1}`,
            text: item,
          };
        }

        return {
          code:
            item.code ||
            item.clo_code ||
            item.clo ||
            item.id ||
            `CLO${index + 1}`,
          text:
            item.text ||
            item.description ||
            item.clo_text ||
            item.statement ||
            item.outcome ||
            "",
        };
      })
      .filter((item) => item.text || item.code);
  }

  if (typeof data === "object") {
    return Object.entries(data).map(([key, val]) => ({
      code: key,
      text:
        typeof val === "string"
          ? val
          : val?.text ||
            val?.description ||
            val?.statement ||
            JSON.stringify(val),
    }));
  }

  return [];
}

function extractCLOsFromCourse(course) {
  if (!course) return [];

  return parseCLOs(
    course.clos ||
      course.CLOs ||
      course.clos_json ||
      course.clo_json ||
      course.course_clos ||
      course.learning_outcomes
  );
}

function extractCLOsFromUploadResponse(data) {
  if (!data) return [];

  return parseCLOs(
    data.clos ||
      data.CLOs ||
      data.extracted_clos ||
      data.course_clos ||
      data.learning_outcomes ||
      data?.course?.clos ||
      data?.course?.CLOs ||
      data?.upload?.clos
  );
}

export default function CourseGuideUpload({ user }) {
  const isCourseLead = useMemo(() => {
    const role = normalizeRole(user?.role);
    return role === "course_lead" || role === "admin";
  }, [user]);

  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [file, setFile] = useState(null);
  const [plans, setPlans] = useState([]);
  const [clos, setClos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedCourse = useMemo(() => {
    return courses.find((c) => String(c.id) === String(courseId)) || null;
  }, [courses, courseId]);

  const loadCourses = async () => {
    setLoadingCourses(true);
    setError("");

    try {
      const res = await api.get("/course-lead/my-courses");
      const list = Array.isArray(res.data) ? res.data : [];

      setCourses(list);

      const nextCourseId = courseId || list[0]?.id || "";
      if (!courseId && nextCourseId) {
        setCourseId(nextCourseId);
      }

      const courseForCLO =
        list.find((c) => String(c.id) === String(nextCourseId)) || list[0];

      setClos(extractCLOsFromCourse(courseForCLO));
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Failed to load courses.");
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadPlans = async (id) => {
    if (!id) {
      setPlans([]);
      return;
    }

    try {
      const res = await api.get(`/course-lead/courses/${id}/weekly-plans`);
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setPlans([]);
    }
  };

  useEffect(() => {
    if (!isCourseLead) return;
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCourseLead]);

  useEffect(() => {
    if (!courseId) {
      setPlans([]);
      setClos([]);
      return;
    }

    loadPlans(courseId);

    const courseClos = extractCLOsFromCourse(selectedCourse);
    setClos(courseClos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, selectedCourse]);

  const upload = async () => {
    if (!courseId) {
      alert("Select course first.");
      return;
    }

    if (!file) {
      alert("Choose course guide file first.");
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await api.post(
        `/course-lead/courses/${courseId}/course-guide/upload`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const uploadedCLOs = extractCLOsFromUploadResponse(res.data);

      if (uploadedCLOs.length) {
        setClos(uploadedCLOs);
      }

      setMessage("Course guide uploaded successfully. Weekly plan and CLOs updated.");

      setFile(null);

      const input = document.getElementById("course-guide-file-input");
      if (input) input.value = "";

      await loadPlans(courseId);
      await loadCourses();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!isCourseLead) {
    return (
      <div className="card">
        <h2>Course Guide Upload</h2>
        <p style={{ opacity: 0.8 }}>
          Only Course Lead or Admin can access this page.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>Course Guide Upload</h2>
        <p style={{ opacity: 0.8 }}>
          Upload master course guide PDF/DOCX. The system generates Week 1–16
          plans and extracts CLOs from the course guide.
        </p>
      </div>

      <div className="card" style={{ display: "grid", gap: 12 }}>
        {error ? (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              background: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        ) : null}

        {message ? (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              background: "#dcfce7",
              color: "#166534",
            }}
          >
            {message}
          </div>
        ) : null}

        <label style={{ fontWeight: 600 }}>Select Course</label>

        <select
          value={courseId}
          onChange={(e) => setCourseId(e.target.value)}
          disabled={loadingCourses || busy}
        >
          {courses.length === 0 ? (
            <option value="">
              {loadingCourses ? "Loading courses..." : "No courses found"}
            </option>
          ) : (
            courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.course_code || "Course"} — {c.course_name || "Untitled"}
              </option>
            ))
          )}
        </select>

        {selectedCourse ? (
          <div style={{ opacity: 0.8, fontSize: 14 }}>
            <strong>Selected:</strong>{" "}
            {selectedCourse.course_code || "Course"} —{" "}
            {selectedCourse.course_name || "Untitled"}
          </div>
        ) : null}

        <label style={{ fontWeight: 600 }}>Upload Course Guide</label>

        <input
          id="course-guide-file-input"
          type="file"
          accept=".pdf,.docx"
          disabled={busy}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />

        {file ? (
          <div style={{ opacity: 0.8, fontSize: 14 }}>
            Selected file: <strong>{file.name}</strong>
          </div>
        ) : null}

        <button
          className="btn-primary"
          onClick={upload}
          disabled={busy || !courseId || !file}
        >
          {busy ? "Uploading..." : "Upload & Generate Weekly Plan"}
        </button>
      </div>

      <div className="card">
        <h3>Extracted CLOs from Course Guide</h3>

        {clos.length === 0 ? (
          <p style={{ opacity: 0.7 }}>
            No CLOs found yet. Upload a course guide or select a course with
            saved CLOs.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {clos.map((clo, index) => (
              <div
                key={`${clo.code || "clo"}-${index}`}
                className="card"
                style={{
                  padding: 12,
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <strong>{clo.code || `CLO${index + 1}`}</strong>
                <div
                  style={{
                    opacity: 0.85,
                    marginTop: 6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {clo.text || "No description available"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h3>Weekly Plan Week 1–16</h3>

        {plans.length === 0 ? (
          <p style={{ opacity: 0.7 }}>
            No weekly plans yet. Upload a course guide first.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {plans.map((p) => (
              <div key={p.id || p.week_number} className="card" style={{ padding: 12 }}>
                <strong>Week {p.week_number}</strong>

                <div
                  style={{
                    opacity: 0.85,
                    marginTop: 6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {p.planned_topics || "No planned topics added."}
                </div>

                {p.planned_assessments ? (
                  <div
                    style={{
                      opacity: 0.8,
                      marginTop: 8,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <strong>Assessments:</strong> {p.planned_assessments}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}